/**
 * Liest eingefuegte Adressen.
 *
 * Was beim Kopieren aus einer Mail tatsaechlich ankommt, ist selten eine
 * saubere Liste. Outlook gibt
 *
 *     Huber, Anna <a.huber@example.at>; Bauer Max <m.bauer@example.at>
 *
 * heraus, Webmailer trennen mit Zeilenumbruch, und wer aus einer Tabelle
 * kopiert, bekommt Anfuehrungszeichen mit. Eine Zerlegung, die nur an Kommas
 * trennt, scheitert genau an dem Weg, fuer den das Feld gedacht ist - und die
 * Bedienerin sieht nur "keine gueltige Adresse".
 *
 * Bewusst hier und nicht im Browser: die Regel gilt fuer jeden Weg, auf dem
 * Adressen hereinkommen, und zwei Fassungen davon wuerden auseinanderlaufen.
 */

/**
 * Zerlegt eingefuegten Text in einzelne Adressen.
 *
 * Liefert auch, was NICHT als Adresse gelesen werden konnte. Ein Bruchstueck
 * still zu verwerfen waere hier besonders heimtueckisch: die Aussendung liefe
 * durch, und erst hinterher faellt auf, dass einer der Empfaenger fehlte.
 *
 * @param {string|string[]} eingabe
 * @returns {{ adressen: string[], verworfen: string[] }}
 */
export function adressenLesen(eingabe) {
  const text = Array.isArray(eingabe) ? eingabe.join('\n') : String(eingabe ?? '');

  // Zuerst alles in spitzen Klammern: dort steht die Adresse, wenn ein
  // Anzeigename davorsteht. Der Name selbst enthaelt haeufig ein Komma
  // ("Huber, Anna") und wuerde beim Trennen sonst zu zwei Bruchstuecken.
  const ausKlammern = [...text.matchAll(/<([^<>]+@[^<>]+)>/g)].map((treffer) => treffer[1] ?? '');
  const ohneKlammern = text.replace(/<[^<>]*>/g, ' ');

  /**
   * Entfernt Anfuehrungszeichen, Aufzaehlungszeichen und mailto:.
   *
   * Die Reihenfolge zaehlt: bei "- mailto:b@x.at" steht das
   * Aufzaehlungszeichen VOR dem mailto:, deshalb wird es zuerst abgeraeumt.
   *
   * @param {string} teil
   * @returns {string}
   */
  const saeubern = (teil) =>
    teil
      .trim()
      .replace(/^["'`•–—\-\s]+/, '')
      .replace(/^mailto:/i, '')
      .replace(/["'`\s.,;]+$/, '')
      .trim();

  /** @type {string[]} */
  const uebrige = [];
  /** @type {string[]} */
  const verworfen = [];

  for (const roh of ohneKlammern.split(/[\n\r,;]+/)) {
    const teil = saeubern(roh);
    if (!teil) continue;
    // Ein Anzeigename ohne Adresse ("Huber, Anna") ist kein Fehler, sondern
    // die zweite Haelfte einer Angabe, deren Adresse in Klammern stand.
    if (teil.includes('@')) {
      uebrige.push(teil);
    } else if (!ausKlammern.length) {
      // Nur melden, wenn nirgends eine Adresse in Klammern stand - sonst
      // wuerde jeder Anzeigename als Fehler erscheinen.
      verworfen.push(teil);
    }
  }

  const alle = [...ausKlammern.map(saeubern), ...uebrige]
    .map((adresse) => adresse.toLowerCase())
    .filter(Boolean);

  return { adressen: [...new Set(alle)], verworfen };
}
